/**
 * Contains helpers that manipulate a given modifier group and modifier options hierarchy
 *
 * All functions that need to traverse between modifierGroups and options should go here
 */

export function ModifierHierarchyHelper() {
    'ngInject';
    let service = {
        initializeModifierHierarchy,
        updateModifierHierarchy,
        getTotalOptionsPrice
    };

    return service;

    /**
     * _updateOptionFlags(): sets the 'selected' flag on any open options
     *   -> If the option has subgroups, sets selected to true if all its subgroups are satisfied
     *   -> If it has no subgroups, the flag is unchanged
     *
     * @param option
     */
    function _updateOptionFlags(option) {
        var i,
            satisfied = true;

        if (option.opened && option.modifierGroups.length > 0) {
            for (i = 0; i < option.modifierGroups.length; i++) {
                if (!option.modifierGroups[i].minSatisfied && option.modifierGroups[i].minSelections > 0) {
                    satisfied = false;
                    break;
                }
            }

            option.selected = satisfied;
        }
    }

    /**
     * _updateModifierGroupFlags(): sets the minSatisfied and maxSelectionsMet flags on the given modifierGroup
     * @param modifierGroup
     */
    function _updateModifierGroupFlags(modifierGroup) {
        modifierGroup.maxSelectionsMet = modifierGroup.numOptionsSelected === modifierGroup.maxSelections;
        modifierGroup.minSatisfied = modifierGroup.numOptionsSelected >= modifierGroup.minSelections;
    }

    /**
     * initializeModifierHierarchy(): recurse down the modifier hierarchy initializing certain fields
     *  -> Sets options that are isDefault to selected and in stock, and opens up options that have subgroups
     *  -> Disables options that are out of stock
     *  -> sets quantity of options with duplicate modifiers enabled to 0 if not selected
     *  -> Returns the price change caused by default option selections
     *
     * @param node: an object with key 'modifierGroups' or 'options'
     */
    function initializeModifierHierarchy(node) {
        var i;

        if (!node) {
            return;
        }

        if (node.hasOwnProperty('options')) {
            node.numOptionsSelected = 0;

            for (i = 0; i < node.options.length; i++) {
                initializeModifierHierarchy(node.options[i]);

                if (node.options[i].selected) {
                    node.numOptionsSelected += 1;
                }
            }

            _updateModifierGroupFlags(node);

            if (node.maxSelections > 1 && node.maxSelectionsMet) {
                _disableOptionsFor(node.options);
            }
        } else if (node.hasOwnProperty('modifierGroups')) {
            // Open the option if it has subgroups
            if (node.modifierGroups.length > 0) {
                for (i = 0; i < node.modifierGroups.length; i++) {
                    initializeModifierHierarchy(node.modifierGroups[i]);
                }

                // This is run after the recursive step to account for subgroups being fulfilled
                if (node.isDefault) {
                    node.opened = true;
                    _updateOptionFlags(node);
                } else {
                    // If an option is not default, it should not be selected even if all its subgroups are satisfied
                    node.opened = false;
                    node.selected = false;
                }
            } else {
                // Select in-stock default options
                node.selected = node.isDefault && node.inventoryCount !== 0;
            }

            if (node.maxSelections > 1 || !node.maxSelections) {
                node.quantity = node.selected ? 1 : 0;
            }

            // Disable out of stock options
            node.disabled = node.inventoryCount === 0;
        }
    }

    function _disableOptionsFor(options) {
        for (var i = 0; i < options.length; i++) {
            options[i].disabled = !options[i].selected;
        }
    }

    /**
     * updateModifierHierarchy(): recurse down the modifier hierarchy, changing flags to account for changes
     *  -> Updates both modifierGroups as well as options
     *  -> If there exist nested modifiers, only update options that are open
     *  -> Returns the price changed by the options selected
     *
     * @param node: takes in either a modifierGroup object or an option object
     */
    function updateModifierHierarchy(node) {
        var i;

        // Base case
        if (!node) {
            return;
        }

        // Determine whether its a modifierGroup or an option
        if (node.hasOwnProperty('options')) {
            node.numOptionsSelected = 0;

            for (i = 0; i < node.options.length; i++) {
                updateModifierHierarchy(node.options[i]);

                if (node.options[i].selected) {
                    node.numOptionsSelected += node.options[i].quantity || 1;
                }
            }

            _updateModifierGroupFlags(node);
        } else if (node.hasOwnProperty('modifierGroups')) {
            for (i = 0; i < node.modifierGroups.length; i++) {
                updateModifierHierarchy(node.modifierGroups[i]);
            }

            _updateOptionFlags(node);
        }
    }

    /**
     * returns the price delta caused by selection of any options under the current item
     */
    function getTotalOptionsPrice(node) {
        var i,
            priceIncrease = 0;

        if (node.hasOwnProperty('options')) {
            for (i = 0; i < node.options.length; i++) {
                priceIncrease += getTotalOptionsPrice(node.options[i]);
            }
        } else if (node.hasOwnProperty('modifierGroups') && node.selected) {
            if (node.modifierGroups.length > 0) {
                for (i = 0; i < node.modifierGroups.length; i++) {
                    // INCLUDED
                    priceIncrease += getTotalOptionsPrice(node.modifierGroups[i]);
                }
                // The priceMode and its prices are computed server-side and baked in.
                if (node.hasOwnProperty('price')) {
                    // ADJUSTS_PRICE, FIXED_PRICE
                    priceIncrease += node.price;
                }
            } else {
                priceIncrease += node.price;
            }

            // Account for repeated modifiers
            if (node.quantity) {
                priceIncrease *= node.quantity;
            }
        }

        return priceIncrease;
    }

}
